import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, ShoppingCart, Coffee, Truck, Package } from 'lucide-react';
import { TagesgeschaeftShareBar } from '../../components/TagesgeschaeftShareBar';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Verfügbare Icons für Kategorien
const AVAILABLE_ICONS = [
  { name: 'ShoppingCart', icon: '🛒' },
  { name: 'Coffee', icon: '☕' },
  { name: 'Truck', icon: '🚚' },
  { name: 'Package', icon: '📦' },
  { name: 'Apple', icon: '🍎' },
  { name: 'Bread', icon: '🥖' },
  { name: 'Milk', icon: '🥛' },
  { name: 'Meat', icon: '🥩' },
];

// Verfügbare Farben
const AVAILABLE_COLORS = [
  { name: 'Rot', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Gelb', value: '#eab308' },
  { name: 'Grün', value: '#22c55e' },
  { name: 'Blau', value: '#3b82f6' },
  { name: 'Lila', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Grau', value: '#6b7280' },
];

export const BestellKategorien = () => {
  const { user } = useAuth();
  const canShare = ['admin', 'stationsleitung', 'superadmin'].includes(user?.role);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    icon: '🛒',
    color: '#3b82f6',
    external_url: '',
    notes: '',
    sort_order: 0,
    enabledForAdmin: true,
    enabledForStationsleitung: true,
    enabledForEmployee: true,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/api/order-categories`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      // Sort by sort_order
      const sorted = (response.data || []).sort((a, b) => a.sort_order - b.sort_order);
      setCategories(sorted);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Fehler beim Laden der Kategorien');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        icon: category.icon,
        color: category.color,
        external_url: category.external_url || '',
        notes: category.notes || '',
        sort_order: category.sort_order || 0,
        supplier_email: category.supplier_email || '',
        kundennummer: category.kundennummer || '',
        enabledForAdmin: category.enabled_for_roles.includes('admin'),
        enabledForStationsleitung: category.enabled_for_roles.includes('stationsleitung'),
        enabledForEmployee: category.enabled_for_roles.includes('employee'),
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        icon: '🛒',
        color: '#3b82f6',
        external_url: '',
        notes: '',
        sort_order: categories.length,
        supplier_email: '',
        kundennummer: '',
        enabledForAdmin: true,
        enabledForStationsleitung: true,
        enabledForEmployee: true,
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    const enabledForRoles = [];
    if (formData.enabledForAdmin) enabledForRoles.push('admin');
    if (formData.enabledForStationsleitung) enabledForRoles.push('stationsleitung');
    if (formData.enabledForEmployee) enabledForRoles.push('employee');

    if (enabledForRoles.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Rolle aus');
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        icon: formData.icon,
        color: formData.color,
        external_url: formData.external_url.trim() || null,
        notes: formData.notes.trim() || null,
        sort_order: parseInt(formData.sort_order) || 0,
        enabled_for_roles: enabledForRoles,
        supplier_email: formData.supplier_email.trim() || null,
        kundennummer: formData.kundennummer.trim() || null,
        customer_id: 'dummy' // Backend setzt die richtige customer_id
      };

      if (editingCategory) {
        await axios.put(`${API}/api/order-categories/${editingCategory.id}`, payload, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Kategorie erfolgreich aktualisiert');
      } else {
        await axios.post(`${API}/api/order-categories`, payload, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Kategorie erfolgreich erstellt');
      }

      setShowDialog(false);
      fetchCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async () => {
    if (!deletingCategory) return;

    try {
      await axios.delete(`${API}/api/order-categories/${deletingCategory.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Kategorie erfolgreich gelöscht');
      setShowDeleteDialog(false);
      setDeletingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Lädt...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 pb-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
              Bestellkategorien
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              Verwalten Sie Lieferanten und Bestellkategorien
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button 
              onClick={() => handleOpenDialog()} 
              className="w-full sm:w-auto"
              variant="default"
            >
              <Plus className="h-4 w-4 mr-2" />
              Neue Kategorie
            </Button>
            <Button 
              onClick={() => window.location.href = '/admin/bestell-produkte'} 
              className="w-full sm:w-auto"
              variant="outline"
            >
              <Package className="h-4 w-4 mr-2" />
              Alle Produkte
            </Button>
          </div>
        </div>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <ShoppingCart className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">💡 Tipp</p>
                <p>
                  Erstellen Sie Kategorien für Ihre Lieferanten (z.B. Cup & Cino, MeinNordland, Frische). 
                  Später können Sie hier Produktkataloge hinterlegen und Bestellungen aufgeben.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Empfangen / Vorlagen von Partnern */}
        {canShare && (
          <TagesgeschaeftShareBar variant="receive" contentType="bestellkategorie" />
        )}

        {/* Categories Grid */}
        {categories.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Noch keine Kategorien</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Erstellen Sie Ihre erste Bestellkategorie
              </p>
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Kategorie erstellen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <Card key={category.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="p-3 rounded-lg text-2xl"
                        style={{ backgroundColor: `${category.color}20` }}
                      >
                        {category.icon}
                      </div>
                      <div>
                        <CardTitle className="text-base sm:text-lg">{category.name}</CardTitle>
                        <p className="text-xs text-muted-foreground mt-1">
                          Reihenfolge: {category.sort_order}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* External URL */}
                    {category.external_url && (
                      <div className="text-xs text-muted-foreground truncate">
                        🔗 {category.external_url}
                      </div>
                    )}
                    {category.notes && (
                      <div className="text-xs text-muted-foreground italic">
                        📝 {category.notes}
                      </div>
                    )}

                    {/* Roles */}
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Sichtbar für</Label>
                      <div className="flex flex-wrap gap-2">
                        {category.enabled_for_roles.includes('admin') && (
                          <Badge variant="secondary" className="text-xs">Admin</Badge>
                        )}
                        {category.enabled_for_roles.includes('stationsleitung') && (
                          <Badge variant="secondary" className="text-xs">Stationsleitung</Badge>
                        )}
                        {category.enabled_for_roles.includes('employee') && (
                          <Badge variant="secondary" className="text-xs">Mitarbeiter</Badge>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDialog(category)}
                        className="flex-1"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Bearbeiten
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDeletingCategory(category);
                          setShowDeleteDialog(true);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Teilen (mit Partner-Kunden) */}
                    {canShare && (
                      <div className="pt-2 border-t border-border/60" data-testid={`share-category-${category.id}`}>
                        <TagesgeschaeftShareBar
                          variant="compact"
                          contentType="bestellkategorie"
                          categoryId={category.id}
                          subjectName={category.name}
                          subjectIcon={category.icon}
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Kategorie bearbeiten' : 'Neue Bestellkategorie'}
            </DialogTitle>
            <DialogDescription>
              Legen Sie eine neue Kategorie für Bestellungen an
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="z.B. Cup & Cino, MeinNordland..."
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            {/* Icon */}
            <div className="space-y-2">
              <Label>Icon/Emoji</Label>
              <div className="grid grid-cols-4 gap-2">
                {AVAILABLE_ICONS.map((item) => (
                  <button
                    key={item.name}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: item.icon })}
                    className={`p-3 rounded-lg border-2 text-2xl transition-colors ${
                      formData.icon === item.icon
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {item.icon}
                  </button>
                ))}
              </div>
            </div>

            {/* Color */}
            <div className="space-y-2">
              <Label>Farbe</Label>
              <div className="grid grid-cols-4 gap-2">
                {AVAILABLE_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.color === color.value
                        ? 'border-gray-800 scale-105'
                        : 'border-gray-200'
                    }`}
                    style={{ backgroundColor: color.value }}
                  >
                    <span className="sr-only">{color.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* External URL */}
            <div className="space-y-2">
              <Label htmlFor="external_url">Externe URL (optional)</Label>
              <Input
                id="external_url"
                type="url"
                placeholder="https://..."
                value={formData.external_url}
                onChange={(e) => setFormData({ ...formData, external_url: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Falls Ihr Lieferant eine eigene Bestell-Website hat
              </p>
            </div>

            {/* Lieferanten Auto-Versand */}
            <div className="space-y-3 rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 p-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">Automatischer E-Mail-Versand an Lieferant</span>
                <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">Optional</span>
              </div>
              <p className="text-xs text-blue-700 dark:text-blue-400">
                Wenn gesetzt, wird nach jeder Bestellung automatisch ein PDF per E-Mail an den Lieferanten gesendet.
              </p>
              <div className="space-y-2">
                <Label htmlFor="supplier_email">Lieferanten-E-Mail</Label>
                <Input
                  id="supplier_email"
                  type="email"
                  placeholder="bestellungen@lieferant.de"
                  value={formData.supplier_email}
                  onChange={(e) => setFormData({ ...formData, supplier_email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="kundennummer">Kundennummer beim Lieferanten</Label>
                <Input
                  id="kundennummer"
                  placeholder="z.B. 12345678"
                  value={formData.kundennummer}
                  onChange={(e) => setFormData({ ...formData, kundennummer: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Wird im PDF und in der E-Mail mit angegeben</p>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notiz (optional)</Label>
              <Input
                id="notes"
                placeholder="z.B. Fremd-Lieferant, Ansprechpartner: Max, Tel: 0711-..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Interne Notiz – z.B. "Fremd-Lieferant", Kontaktdaten, Besonderheiten
              </p>
            </div>

            {/* Sort Order */}
            <div className="space-y-2">
              <Label htmlFor="sort_order">Reihenfolge</Label>
              <Input
                id="sort_order"
                type="number"
                min="0"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Niedrigere Zahlen erscheinen weiter oben im Menü
              </p>
            </div>

            {/* Roles */}
            <div className="space-y-3">
              <Label>Sichtbar für *</Label>
              
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm font-medium">Administratoren</span>
                <Switch
                  checked={formData.enabledForAdmin}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, enabledForAdmin: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm font-medium">Stationsleitung</span>
                <Switch
                  checked={formData.enabledForStationsleitung}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, enabledForStationsleitung: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm font-medium">Mitarbeiter</span>
                <Switch
                  checked={formData.enabledForEmployee}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, enabledForEmployee: checked })
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave}>
              {editingCategory ? 'Speichern' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kategorie löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie die Kategorie "{deletingCategory?.name}" wirklich löschen? 
              Später hinzugefügte Produkte bleiben erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingCategory(null)}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};
